import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Linkify from 'react-linkify';
import { theme } from 'theme';

import {
  Extension,
  StyledButton,
  StyledDescriptionText,
  StyledDots,
  StyledParagraph,
  StyledText,
} from './styled';

type ParagraphProps = {
  text: string;
  rows: number;
  style?: React.CSSProperties;
  backgroundColor?: string;
  textColor?: string;
  textStyle?: React.CSSProperties;
  showMoreLess?: boolean;
};

const Paragraph: React.FC<ParagraphProps> = ({
  text,
  rows,
  style,
  backgroundColor = theme.color.white,
  textColor = theme.color.black,
  textStyle,
  showMoreLess = true,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isEllipsis, setIsEllipsis] = useState<boolean>(false);
  const paragraphRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState<string>('0');

  const toggleExpand = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  useEffect(() => {
    const paragraphElement = paragraphRef.current;

    if (paragraphElement && lineHeight === '0') {
      const computedStyle = window.getComputedStyle(paragraphElement);
      const newLineHeight = computedStyle.lineHeight;

      setTimeout(() => {
        setLineHeight(newLineHeight);
      }, 50);
    }

    if (
      paragraphElement &&
      paragraphElement.scrollHeight > paragraphElement.clientHeight &&
      lineHeight !== '0'
    ) {
      setIsEllipsis(true);
    } else {
      setIsEllipsis(false);
    }

    return () => {
      setIsEllipsis(false);
    };
  }, [lineHeight, paragraphRef]);

  const getMaxHeight = () => {
    if (!showMoreLess) {
      return 'none';
    }
    if (expanded) {
      return isEllipsis ? 'none' : '20px';
    } else {
      return `${parseInt(lineHeight) * rows}px`;
    }
  };

  return (
    <StyledParagraph
      ref={paragraphRef}
      maxHeight={getMaxHeight()}
      overflow={showMoreLess ? (expanded ? 'visible' : 'hidden') : 'visible'}
      style={style}
    >
      <StyledText
        ref={textRef}
        backgroundColor={backgroundColor}
        style={textStyle}
      >
        <Linkify
          componentDecorator={(decoratedHref, decoratedText, key) => (
            <a target="blank" href={decoratedHref} key={key}>
              {decoratedText}
            </a>
          )}
        >
          <StyledDescriptionText>
            {text}
            {expanded && showMoreLess && (
              <span style={{ marginLeft: '4px' }}>
                <StyledButton
                  onClick={toggleExpand}
                  backgroundColor={backgroundColor}
                >
                  {expanded ? t('common.less') : t('common.more')}
                </StyledButton>
              </span>
            )}
          </StyledDescriptionText>
        </Linkify>
      </StyledText>
      {isEllipsis && !expanded && showMoreLess && (
        <Extension backgroundColor={backgroundColor} textColor={textColor}>
          <StyledDots>...</StyledDots>
          <StyledButton onClick={toggleExpand}>
            {expanded ? t('common.less') : t('common.more')}
          </StyledButton>
        </Extension>
      )}
    </StyledParagraph>
  );
};

export default Paragraph;
